import {
  HHAccordion,
  HHAccordionDetails,
  HHAccordionSummary,
  HHCard,
  HHCardContent,
  HHCardHeader,
  HHDialog,
  HHDialogContent,
  HHDialogTitle,
  HHDivider,
  HHIconButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { ExpandMore } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import ReadMore from '@mui/icons-material/ReadMore';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useState } from 'react';
import {
  ActiveContractLabels,
  MilestoneLabels,
} from '../../../../constants/strings/billing-label-enums';
import {
  BillingModelType,
  ContractPackagesType,
  ContractTemplateType,
  ContractType,
  EngagementType,
  MilestoneConditionType,
  PackageOfferingsType,
  PackageType,
  useProductOfferingsQuery,
} from '../../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../../utils/bills-utils';
import {
  isEngagementModelById,
  isFeeForServiceModelById,
  isMilestoneModelByName,
} from '../../../../utils/contract-utils';
import { formatCurrency } from '../../../../utils/currency-helpers';
import {
  getApplicableDiscounts,
  IDiscount,
} from '../../../../utils/discount-utils';
import {
  categorizePackageIndications,
  listifyIndications,
} from '../../../../utils/package-utils';
import { getProductOfferingIdsForPackage } from '../../../../utils/scope-utils';
import { TextGrouping } from '../../../utils/text-grouping';
import {
  ConfigurableActivityDisplay,
  PackageBillableActivityDisplay,
} from '../../contract-view-components/billable-activity-display';
import { CostShareOptionsDisplaySingleRow } from '../../contract-view-components/cost-share-options-display';
import { PackageDiscountSummary } from '../../contract-view-components/package-discount-summary';
import {
  BillableActivitiesWithDetails,
  ContractTypeExtension,
  RawInnerPackageFormValues,
  ValidBillingModel,
} from '../custom-package-types';

export interface FormPanelDetailsProps {
  selectedPackage: RawInnerPackageFormValues;
  availablePackages: PackageType[];
  engagementTypes: EngagementType[];
  currency: string | Maybe<string>;
  billingModels: BillingModelType[];
  templates: ContractTemplateType[];
  sx?: SystemStyleObject<Theme>;
  currentContract?: ContractType;
  discounts?: IDiscount[];
  contractStartDate?: Date | null;
  contractEndDate?: Date | null;
}
interface SelectedPackageIndicationsProps {
  packageOfferings: PackageOfferingsType[];
}

interface PricesDisplayProps {
  selectedPackage: RawInnerPackageFormValues;
  currency: string | Maybe<string>;
  billingModels: BillingModelType[];
}

interface SelectedBillableActivitiesDisplayProps {
  selectedBillableActivities: BillableActivitiesWithDetails;
  engagementTypes: EngagementType[];
}

export const SelectedPackageIndications = ({
  packageOfferings,
}: SelectedPackageIndicationsProps): JSX.Element => {
  const [indicationsOpen, setIndicationsOpen] = useState(false);

  const indicationCategories = categorizePackageIndications(packageOfferings);

  return (
    <HHStack direction="row" alignItems="center">
      <HHTypography hhVariant="body2">
        {ActiveContractLabels.INDICATIONS_INCLUDED}
      </HHTypography>
      <HHIconButton
        aria-label="read-more"
        hhVariant="variant-bypass"
        onClick={(): void => setIndicationsOpen(true)}
        size="small"
      >
        <ReadMore fontSize="small" />
      </HHIconButton>
      <HHDialog
        open={indicationsOpen}
        onClose={(): void => setIndicationsOpen(false)}
        fullWidth
        keepMounted={false}
      >
        <HHDialogTitle>
          <HHStack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <HHTypography hhVariant="muted" fontSize={12}>
              Included Program Indications
            </HHTypography>
            <HHIconButton
              aria-label="close"
              onClick={(): void => setIndicationsOpen(false)}
              hhVariant={'medium'}
            >
              <Close />
            </HHIconButton>
          </HHStack>
        </HHDialogTitle>
        <HHDialogContent>
          <HHStack
            spacing={2}
            padding={1}
            direction="column"
            justifyContent="flex-start"
            sx={(theme): SystemStyleObject<Theme> => ({
              backgroundColor: theme.palette.background.default,
            })}
          >
            {Object.keys(indicationCategories).map((key, i, og) => (
              <HHStack
                direction="column"
                spacing={2}
                flexWrap="wrap"
                padding={1}
                key={key}
                divider={og.length === i ? null : <HHDivider />}
              >
                <HHTypography hhVariant="muted" fontSize={12}>
                  {capitalizeFirstLetterAndSpaceSnakeString(key)}
                </HHTypography>
                <HHTypography hhVariant="body2">
                  {listifyIndications(indicationCategories[key])}
                </HHTypography>
              </HHStack>
            ))}
          </HHStack>
        </HHDialogContent>
      </HHDialog>
    </HHStack>
  );
};

const SelectedBillableActivitiesDisplay = ({
  selectedBillableActivities,
  engagementTypes,
}: SelectedBillableActivitiesDisplayProps): JSX.Element => {
  const billableActivitiesToDisplay = engagementTypes
    .filter(ent => ent.id in selectedBillableActivities)
    .map(ent => ({
      ...ent,
      details: selectedBillableActivities[ent.id],
    }));

  return (
    <HHStack direction="column" paddingTop={4}>
      <PackageBillableActivityDisplay
        packageBillableActivities={billableActivitiesToDisplay}
      />
    </HHStack>
  );
};

const PricesDisplay = ({
  selectedPackage,
  currency,
  billingModels,
}: PricesDisplayProps): JSX.Element => {
  const isEngagementPackage = isEngagementModelById(
    billingModels,
    selectedPackage.billingModelId,
  );

  return (
    <HHStack spacing={4} paddingTop={2}>
      <TextGrouping
        label="Price"
        text={formatCurrency(selectedPackage.price, currency)}
      />
      {isEngagementPackage && (
        <HHStack direction="row" spacing={4} flex={1}>
          <TextGrouping
            label={ActiveContractLabels.ENGAGEMENT_INITIAL_FEE}
            text={formatCurrency(selectedPackage.initialFee, currency)}
          />
          <TextGrouping
            label={ActiveContractLabels.ENGAGEMENT_ACTIVITY_FEE}
            text={formatCurrency(selectedPackage.activityFee, currency)}
          />
        </HHStack>
      )}
    </HHStack>
  );
};

export const panelConditionsMap: Record<string, string> = {
  enrollment_check: 'Enrollment',
  engagement_threshold_check: 'Engagement threshold',
  pain_reduction_check: 'Pain reduction',
};

const FormPanelDetails = ({
  selectedPackage,
  availablePackages,
  engagementTypes,
  currency,
  billingModels,
  templates,
  sx,
  currentContract,
  discounts,
  contractStartDate,
  contractEndDate,
}: FormPanelDetailsProps): JSX.Element => {
  const selectedBillingModel = billingModels.find(
    model => model.id === selectedPackage.billingModelId,
  );

  const isMilestoneModel = isMilestoneModelByName(
    selectedBillingModel?.name as ValidBillingModel,
  );

  const billingModelName = selectedBillingModel?.name;

  const template =
    templates.find(
      t => t.id === selectedPackage.currentTemplate?.contractTemplateId,
    ) ?? null;

  const isOtherTemplateType =
    template?.name.toLowerCase().includes('other') ?? false;

  const existingContractRules = currentContract?.contractPackages.find(
    (cp: ContractPackagesType) =>
      cp.id === selectedPackage.id && cp.contractTemplateId === template?.id,
  )?.rules;

  let contractRules: ContractTypeExtension[] = [];
  if (isOtherTemplateType && Array.isArray(existingContractRules)) {
    contractRules = existingContractRules as ContractTypeExtension[];
  } else if (!isOtherTemplateType && Array.isArray(template?.contract)) {
    contractRules = template?.contract as ContractTypeExtension[];
  }

  const isFeeForService =
    billingModelName === ValidBillingModel.FEE_FOR_SERVICE;

  const { data: productOfferingsData } = useProductOfferingsQuery();
  const productOfferingIds = getProductOfferingIdsForPackage(
    availablePackages.find(p => p.id === selectedPackage.packageId)
      ?.packageOfferings ?? [],
    productOfferingsData?.productOfferings ?? [],
  );

  const applicableDiscounts =
    !isFeeForService && selectedPackage.packageId && contractStartDate
      ? getApplicableDiscounts(
          discounts,
          selectedPackage.packageId,
          contractStartDate,
          contractEndDate,
          productOfferingIds,
        )
      : [];

  return (
    <HHCard
      data-testid="contract-form-right-panel"
      sx={(theme): SystemStyleObject<Theme> => ({
        ...sx,
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(4),
        flex: 1,
        overflowY: 'auto',
      })}
    >
      <HHCardHeader
        title={
          <HHStack textTransform="capitalize">
            {Boolean(selectedPackage.packageId) && (
              <HHTypography hhVariant="subtitle1">
                {`${availablePackages
                  .find(ap => ap.id === selectedPackage.packageId)
                  ?.name.replaceAll('_', ' ')} package`}
              </HHTypography>
            )}
            {Boolean(selectedPackage.billingModelId) && (
              <HHTypography hhVariant="body2">
                {`${billingModelName?.replaceAll('_', ' ')}`}
              </HHTypography>
            )}
          </HHStack>
        }
        sx={{ paddingBottom: 0 }}
      ></HHCardHeader>
      <HHCardContent>
        <SelectedPackageIndications
          packageOfferings={
            availablePackages.find(p => p.id === selectedPackage.packageId)
              ?.packageOfferings || []
          }
        />
        {isMilestoneModel && template ? (
          <>
            {contractRules.length === 0 ? (
              <HHTypography hhVariant="body2">
                No milestones in this template
              </HHTypography>
            ) : (
              contractRules.map((milestone: ContractTypeExtension) => (
                <HHStack
                  flexDirection="column"
                  spacing={2}
                  key={milestone.name}
                  flex={1}
                  padding={1}
                >
                  <HHDivider />
                  <HHAccordion
                    key={milestone.name}
                    sx={(theme): SystemStyleObject<Theme> => ({
                      border: 'none',
                      boxShadow: 'none',
                      backgroundColor: theme.palette.background.default,
                    })}
                  >
                    <HHAccordionSummary expandIcon={<ExpandMore />}>
                      <HHStack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <TextGrouping
                          label={'M' + milestone.name.replace('C', '')}
                          text={formatCurrency(milestone.payment, currency)}
                        />
                        <ConfigurableActivityDisplay
                          milestone={milestone}
                          selectedBillableActivities={
                            selectedPackage.selectedBillableActivities
                          }
                          engagementTypes={engagementTypes}
                        />
                      </HHStack>
                    </HHAccordionSummary>
                    <HHAccordionDetails>
                      <HHStack direction="row">
                        <HHStack direction="column" flex={1}>
                          <HHTypography hhVariant="muted">
                            {MilestoneLabels.DOS_OFFSET}
                          </HHTypography>
                          <HHTypography hhVariant="body2">
                            {milestone.dateOfServiceOffset
                              ? `${milestone.dateOfServiceOffset}`
                              : 'None'}
                          </HHTypography>
                        </HHStack>
                        <HHStack direction="column">
                          <HHTypography hhVariant="muted">
                            {MilestoneLabels.DAY_RANGE}
                          </HHTypography>
                          <HHTypography hhVariant="body2">
                            {milestone.dayRange &&
                            milestone.dayRange.length >= 1
                              ? `${milestone.dayRange[0]} to ${milestone.dayRange[1]} days`
                              : 'None'}
                          </HHTypography>
                        </HHStack>
                      </HHStack>
                      <HHStack>
                        <HHTypography hhVariant="muted">
                          {MilestoneLabels.BILLING_CONDITIONS}
                        </HHTypography>
                        {milestone.conditions?.map(
                          (c: MilestoneConditionType) => (
                            <HHTypography hhVariant="body2" key={c.function}>
                              {panelConditionsMap[c.function]} -{' '}
                              {panelConditionsMap[c.function] ===
                              panelConditionsMap.pain_reduction_check
                                ? c.threshold + '%'
                                : c.threshold + ' billable activity'}
                            </HHTypography>
                          ),
                        )}
                      </HHStack>
                    </HHAccordionDetails>
                  </HHAccordion>
                </HHStack>
              ))
            )}
          </>
        ) : (
          <>
            <HHDivider sx={{ with: '100%' }} />
            <PricesDisplay
              selectedPackage={selectedPackage}
              currency={currency}
              billingModels={billingModels}
            />
            <TextGrouping
              label="Billing Type Override"
              text={capitalizeFirstLetterAndSpaceSnakeString(
                selectedPackage.billingTypeOverride ?? 'None',
              )}
            />
            {isFeeForServiceModelById(
              billingModels,
              selectedPackage.billingModelId,
            ) &&
              selectedPackage.costShareRequired &&
              selectedPackage.cadence &&
              selectedPackage.deductionTiming && (
                <HHStack spacing={2} paddingTop={2}>
                  <CostShareOptionsDisplaySingleRow
                    cadence={selectedPackage.cadence}
                    deductionTiming={selectedPackage.deductionTiming}
                  />
                </HHStack>
              )}
            <SelectedBillableActivitiesDisplay
              selectedBillableActivities={
                selectedPackage.selectedBillableActivities
              }
              engagementTypes={engagementTypes}
            />
          </>
        )}
        {applicableDiscounts.length > 0 && (
          <>
            <HHDivider sx={{ marginTop: 2 }} />
            <HHStack paddingTop={2}>
              <PackageDiscountSummary discounts={applicableDiscounts} />
            </HHStack>
          </>
        )}
      </HHCardContent>
    </HHCard>
  );
};

export default FormPanelDetails;
